import { Segmented, Tooltip } from 'antd';
import {
  FARM_DIAGNOSTICS_COST_DISPLAY_PER_HA_LABEL,
  FARM_DIAGNOSTICS_COST_DISPLAY_TOTAL_LABEL,
  SUMMARY_ENTITY_LABELS,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import React, { useMemo, useState } from 'react';
import { formatCurrency } from 'utils/CurrencyUtils';
import {
  computeGrossMarginForInputs,
  toGmPerHa,
  type GmAreaBasis,
} from 'pages/FarmsPage/Workspace/Insights/computeGrossMargin';
import { groupCostLinesByCategory } from 'pages/FarmsPage/Workspace/Insights/costCategories';

/** Whether the 3×3 matrix shows total $ or $/ha (OP-2393). */
type GmValueMode = 'total' | 'perHa';

// Plain small Segmented controls with the same labels as Farm Diagnostics'
// Yearly Overview, so every Plan/Actual and Total/$/ha toggle on the Insights
// and Diagnostics tabs looks the same.
const CATEGORY_OPTIONS = [
  { label: 'Plan', value: InputTaskCategory.Plan },
  { label: 'Actual', value: InputTaskCategory.Actual },
];

const GM_VALUE_MODE_OPTIONS: { label: string; value: GmValueMode }[] = [
  { label: FARM_DIAGNOSTICS_COST_DISPLAY_TOTAL_LABEL, value: 'total' },
  { label: FARM_DIAGNOSTICS_COST_DISPLAY_PER_HA_LABEL, value: 'perHa' },
];

interface GrossMarginMatrixProps {
  /** Every plan to aggregate — a single-element array for one open plan, or every plan linked to a paddock when scoped there. */
  inputs: IInput[];
  activeCategory: InputTaskCategory;
  onCategoryChange: (category: InputTaskCategory) => void;
  gUnits: GUnit[];
  readOnly?: boolean;
  /**
   * `total` when the plans sit on different paddocks (a crop node): the
   * heading shows their combined area and $/ha falls back to it. Default
   * `average` suits one paddock's plans, which share a footprint.
   */
  areaBasis?: GmAreaBasis;
}

/** Alternating row shading (rather than divider lines) — same bg-white/bg-gray-50 convention used across the Live Panel and Summary tables. */
const rowShade = (idx: number): string =>
  idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

const YIELD_LABELS = ['Yield Low', 'Yield Expected', 'Yield High'] as const;
const PRICE_LABELS = ['Price Low', 'Price Expected', 'Price High'] as const;
const INCOME_LABELS = ['Income Low', 'Income Expected', 'Income High'] as const;

const tooltipContent = (line1: string, line2: string) => (
  <>
    <div>{line1}</div>
    <div>{line2}</div>
  </>
);

const TOOLTIP_AREA = tooltipContent(
  'Average area — inputs use different area values.',
  '= Σ(area) ÷ input count'
);
const TOOLTIP_PRICE = tooltipContent(
  'Area-weighted average price across multiple production estimates.',
  '= Σ(area × price) ÷ Σ(area)'
);
const TOOLTIP_YIELD = tooltipContent(
  'Area-weighted average yield across multiple production estimates.',
  '= Σ(area × yield) ÷ Σ(area)'
);
const TOOLTIP_COST = tooltipContent(
  'Area-weighted average cost across multiple inputs of this category.',
  '= Σ(cost × rate × area) ÷ Σ(area)'
);

const Approx: React.FC<{ tooltip: React.ReactNode }> = ({ tooltip }) => (
  <Tooltip title={tooltip}>
    <span className="cursor-help font-serif text-gray-muted">≈ </span>
  </Tooltip>
);

// Constructed once rather than per call: the paddock preview renders one
// matrix per plan per paddock, and each one calls this for the reference area
// and every reference price and yield (OP-2381).
const TWO_DECIMAL_FORMAT = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmt2 = (value: number): string => TWO_DECIMAL_FORMAT.format(value);

const GrossMarginMatrix: React.FC<GrossMarginMatrixProps> = ({
  inputs,
  activeCategory,
  onCategoryChange,
  gUnits,
  readOnly = false,
  areaBasis = 'average',
}) => {
  const categories = useMemo(() => new Set([activeCategory]), [activeCategory]);

  const data = useMemo(
    () => computeGrossMarginForInputs(inputs, categories, gUnits),
    [inputs, categories, gUnits]
  );

  // The read-only preview page always shows total $ — the toggle is hidden there.
  const [valueMode, setValueMode] = useState<GmValueMode>('total');
  const gmPerHa = useMemo(
    () => toGmPerHa(data, areaBasis),
    [data, areaBasis]
  );
  const headingArea =
    areaBasis === 'total' ? data.refAreaTotal : data.refArea;
  const costByCategory = useMemo(
    () => groupCostLinesByCategory(data.costBreakdown),
    [data]
  );
  const showPerHa = !readOnly && valueMode === 'perHa';

  const HeadingTag: 'h2' | 'span' = readOnly ? 'h2' : 'span';
  const headingClassName = readOnly
    ? 'text-lg font-bold text-charcoal underline underline-offset-4'
    : 'text-base font-bold text-charcoal';
  const labelClassName = readOnly
    ? 'py-1.5 pr-6 text-sm text-gray-dark'
    : 'py-1.5 pr-6 text-base text-gray-muted';
  const footerLabelClassName = readOnly
    ? 'py-1.5 pr-6 text-right font-bold text-sm text-charcoal'
    : 'py-1.5 pr-6 text-right font-bold text-base text-gray-dark';
  // Parenthetical sub-values (reference area/price/yield) need to stay legible
  // against the panel's white background — text-xs font-semibold text-secondary-dark
  // matches the Right Panel's own section-heading treatment (e.g. "Break-Even
  // Yield") for weight/colour, sized down further since this is inline detail
  // rather than a heading. Replaces the previous font-normal text-gray-muted,
  // which read too light/low-contrast to prioritise accessibility.
  const subValueClassName = readOnly
    ? 'text-gray-dark'
    : 'text-xs font-semibold text-secondary-dark';
  const valueFontClassName = 'tabular-nums';
  const tableTextClassName = readOnly ? 'text-sm' : 'text-base';
  const headerCellClassName = readOnly
    ? 'text-xs font-bold text-charcoal'
    : 'text-sm font-bold text-charcoal';

  return (
    <div className="flex flex-col gap-4 self-start">
      <HeadingTag className={headingClassName}>
        Gross Margin Summary
        {headingArea > 0 && (
          <span className={`ml-1 ${subValueClassName}`}>
            (
            {areaBasis === 'average' && data.refAreaIsApproximate && (
              <Approx tooltip={TOOLTIP_AREA} />
            )}
            {fmt2(headingArea)} ha)
          </span>
        )}
      </HeadingTag>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3">
          <Segmented<InputTaskCategory>
            size="small"
            aria-label="Plan or actual"
            options={CATEGORY_OPTIONS}
            value={activeCategory}
            onChange={onCategoryChange}
          />
          <Segmented<GmValueMode>
            size="small"
            aria-label="Gross margin units"
            options={GM_VALUE_MODE_OPTIONS}
            value={valueMode}
            onChange={setValueMode}
          />
        </div>
      )}
      {/* 3×3 GM matrix */}
      <table className={`w-auto border-collapse ${tableTextClassName}`}>
        <thead>
          <tr>
            <th className={`pb-2 pr-6 text-left ${headerCellClassName}`}>
              {showPerHa ? 'GROSS MARGIN ($/ha)' : 'GROSS MARGIN ($)'}
            </th>
            {PRICE_LABELS.map((label, pi) => (
              <th
                key={label}
                className={`w-24 px-2 pb-2 text-center ${headerCellClassName}`}
              >
                {label}
                {data.refPrices[pi] !== 0 && (
                  <span
                    className={`block ${subValueClassName}`}
                  >
                    (
                    {data.productionStepCount > 1 && (
                      <Approx tooltip={TOOLTIP_PRICE} />
                    )}
                    ${fmt2(data.refPrices[pi])})
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.gm.map((row, yi) => (
            <tr key={yi} className={`hover:bg-rowHover ${rowShade(yi)}`}>
              <td className={labelClassName}>
                {YIELD_LABELS[yi]}
                {data.refYields[yi] !== 0 && (
                  <span className={`ml-1 ${subValueClassName}`}>
                    (
                    {data.productionStepCount > 1 && (
                      <Approx tooltip={TOOLTIP_YIELD} />
                    )}
                    {fmt2(data.refYields[yi])} {data.yieldUnit}/ha)
                  </span>
                )}
              </td>
              {row.map((totalValue, pi) => {
                // $/ha with no area to divide by shows a dash rather than $0.
                const value = showPerHa ? gmPerHa?.[yi][pi] ?? null : totalValue;
                const isNegative = value !== null && value < 0;
                return (
                  <td
                    key={pi}
                    className={[
                      `w-24 px-2 py-1.5 text-right ${valueFontClassName}`,
                      isNegative ? 'text-danger' : 'text-charcoal',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {value === null ? (
                      <span className="text-gray-muted">—</span>
                    ) : (
                      formatCurrency(value)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Cost breakdown grid */}
      {data.costBreakdown.length > 0 && (
        <table className={`w-auto border-collapse ${tableTextClassName}`}>
          <thead>
            <tr>
              <th className={`pb-2 pr-6 text-left ${headerCellClassName}`}>
                COSTS
              </th>
              <th className={`w-24 px-2 pb-2 text-right ${headerCellClassName}`}>
                $
              </th>
              <th className={`w-24 px-2 pb-2 text-right ${headerCellClassName}`}>
                $/ha
              </th>
            </tr>
          </thead>
          <tbody>
            {costByCategory.map((line, idx) => (
              <tr key={line.category} className={`hover:bg-rowHover ${rowShade(idx)}`}>
                <td className={labelClassName}>
                  {SUMMARY_ENTITY_LABELS[line.category]}
                </td>
                <td
                  className={`w-24 px-2 py-1.5 text-right ${valueFontClassName} text-charcoal`}
                >
                  {formatCurrency(line.totalCost)}
                </td>
                <td
                  className={`w-24 px-2 py-1.5 text-right ${valueFontClassName} text-charcoal`}
                >
                  {line.stepCount > 1 && <Approx tooltip={TOOLTIP_COST} />}
                  {formatCurrency(line.costPerHa)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className={footerLabelClassName}>Total Variable Costs</td>
              <td
                className={`w-24 px-2 py-1.5 text-right ${valueFontClassName} font-bold text-charcoal`}
              >
                {formatCurrency(
                  data.costBreakdown.reduce((sum, l) => sum + l.totalCost, 0)
                )}
              </td>
              <td
                className={`w-24 px-2 py-1.5 text-right ${valueFontClassName} font-bold text-charcoal`}
              >
                {formatCurrency(
                  data.costBreakdown.reduce((sum, l) => sum + l.costPerHa, 0)
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {/* Income breakdown (Low/Expected/High) — mirrors the Cost section's
          structure/styling. No footer total row: unlike cost-by-type lines
          (which are additive components of one total), the three rows here
          are alternative scenarios for the same input, so summing them
          wouldn't produce a meaningful figure. Unlike the Cost table (which
          is omitted entirely when empty), this section always renders — an
          empty state fills the body instead of the whole table disappearing,
          since Income is always a relevant heading for this view. */}
      <table className={`w-auto border-collapse ${tableTextClassName}`}>
        <thead>
          <tr>
            <th className={`pb-2 pr-6 text-left ${headerCellClassName}`}>
              INCOME
            </th>
            <th className={`w-24 px-2 pb-2 text-right ${headerCellClassName}`}>
              $
            </th>
            <th className={`w-24 px-2 pb-2 text-right ${headerCellClassName}`}>
              $/ha
            </th>
          </tr>
        </thead>
        <tbody>
          {data.revenueArea > 0 ? (
            INCOME_LABELS.map((label, si) => {
              const total = data.revenue[si][si];
              const perHa = total / data.revenueArea;
              return (
                <tr key={label} className={`hover:bg-rowHover ${rowShade(si)}`}>
                  <td className={labelClassName}>{label}</td>
                  <td
                    className={`w-24 px-2 py-1.5 text-right ${valueFontClassName} text-charcoal`}
                  >
                    {formatCurrency(total)}
                  </td>
                  <td
                    className={`w-24 px-2 py-1.5 text-right ${valueFontClassName} text-charcoal`}
                  >
                    {formatCurrency(perHa)}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr className={rowShade(0)}>
              <td colSpan={3} className="py-3 text-center text-base text-gray-muted">
                No income data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GrossMarginMatrix;
